import { useAuth } from '../../context/AuthContext';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  ToggleButtonGroup, ToggleButton, TextField, InputAdornment, Button,
} from '@mui/material';

const CARD = { backgroundColor: '#131318', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 2, overflow: 'hidden' };
const TH = {
  backgroundColor: '#0e0e12', color: '#ffffff', fontWeight: 700, fontSize: '0.68rem',
  letterSpacing: '0.06em', textTransform: 'uppercase', py: 0.9, px: 1,
  borderBottom: '1px solid rgba(245,166,35,0.20)', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 2,
};
const SCROLLBAR = {
  scrollbarWidth: 'thin', scrollbarColor: 'rgba(245,166,35,0.3) transparent',
  '&::-webkit-scrollbar': { width: 4, height: 4 },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(245,166,35,0.3)', borderRadius: 8 },
};

const POS_FILTERS = ['ALL', 'QB', 'WR', 'RB', 'TE', 'OT', 'OG', 'C', 'DE', 'DT', 'LB', 'CB', 'S'];

const SLEEPER_PLAYERS_URL = 'https://api.sleeper.app/v1/players/nfl';

let _cache = null;
let _faCache = null;
let _signedCache = null;



function getNrForPlayer(name, faPlayers) {
  if (!faPlayers) return null;
  const norm = n => n.toLowerCase().replace(/[.'`']/g,'').replace(/\s+/g,' ').trim();
  const match = faPlayers.find(p => norm(p.name) === norm(name));
  return match?.nerdRating ?? null;
}

function getNrRank(name, faPlayers) {
  if (!faPlayers) return null;
  const norm = n => n.toLowerCase().replace(/[.'`']/g,'').replace(/\s+/g,' ').trim();
  const match = faPlayers.find(p => norm(p.name) === norm(name));
  if (!match?.nerdRating) return null;
  // Rank among same position group (or all if no pos match)
  const pos = match.posGroup || match.pos;
  const pool = faPlayers.filter(p => (p.posGroup||p.pos) === pos && p.nerdRating != null)
    .sort((a,b) => b.nerdRating - a.nerdRating);
  const rank = pool.findIndex(p => norm(p.name) === norm(name)) + 1;
  return rank > 0 ? rank : null;
}

const FREE_PREVIEW_ROWS = 10;

export default function ConsensusBoard() {
  const { isPremium, isBasic, setAuthModalOpen, setUpgradeModalOpen } = useAuth();
  const [data, setData]           = useState(null);
  const [faPlayers, setFa]        = useState([]);
  const [signedNames, setSigned]  = useState(_signedCache || new Set());
  const [loading, setLoading]     = useState(true);
  const [pos, setPos]             = useState('ALL');
  const [orderBy, setOrderBy]     = useState('consensus');
  const [order, setOrder]     = useState('asc'); // lower consensus rank = better
  const [search, setSearch]   = useState('');

  useEffect(() => {
    const loads = [];
    if (!_cache) {
      loads.push(fetch('/data/analyst_rankings_2026.json').then(r=>r.json()).then(d=>{ _cache=d; setData(d); }));
    } else { setData(_cache); }
    if (!_faCache) {
      loads.push(
        fetch('/data/free_agents_2026.json').then(r=>r.json()).then(d=>{ _faCache=d.players; setFa(d.players); })
      );
    } else { setFa(_faCache); }
    if (!_signedCache) {
      loads.push(
        fetch(SLEEPER_PLAYERS_URL)
          .then(r => r.json())
          .then(data => {
            const s = new Set();
            Object.values(data).forEach(p => {
              if (p.team && p.full_name) {
                s.add(p.full_name.toLowerCase().trim());
              }
            });
            _signedCache = s;
            setSigned(s);
          })
          .catch(() => {})
      );
    }
    Promise.all(loads).finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} sx={{ color: '#f5a623' }} /></Box>;

  // Compute consensus rank + attach NR
  const players = data.players.map(p => {
    const ranks = [p.prisco, p.rosenthal, p.bowen, p.pff].filter(r => r != null);
    const consensus = ranks.length ? +(ranks.reduce((s,r)=>s+r,0) / ranks.length).toFixed(1) : null;
    const nr = getNrForPlayer(p.name, faPlayers);
    // eslint-disable-next-line no-unused-vars
  const nrRank = getNrRank(p.name, faPlayers);
    // Agreement: low std dev = analysts agree; high = disagreement
    const diff = (p.prisco != null && p.rosenthal != null) ? Math.abs(p.prisco - p.rosenthal) : null;
    return { ...p, consensus, nr, diff, rankCount: ranks.length };
  });

  const posFilter = p => {
    if (pos === 'ALL') return true;
    return p.pos === pos;
  };

  // Consensus rank = position in the full consensus-sorted list (across ALL pos filters)
  const isSignedOff = p => {
    if (!p.name || signedNames.size === 0) return false;
    const nl = p.name.toLowerCase().trim();
    if (signedNames.has(nl)) return true;
    for (const sn of signedNames) {
      if (sn.length > 4 && (sn.includes(nl) || nl.includes(sn))) return true;
    }
    return false;
  };

  const allByConsensus = [...players]
    .filter(p => !p.tagged && !isSignedOff(p))
    .sort((a,b) => (a.consensus ?? Infinity) - (b.consensus ?? Infinity));
  const consensusRankMap = {};
  allByConsensus.forEach((p, i) => { consensusRankMap[p.name] = i + 1; });

  const sorted = [...players]
    .filter(p => !p.tagged)
    .filter(p => {
      if (!p.name || signedNames.size === 0) return true;
      const nl = p.name.toLowerCase().trim();
      if (signedNames.has(nl)) return false;
      for (const sn of signedNames) {
        if (sn.length > 4 && (sn.includes(nl) || nl.includes(sn))) return false;
      }
      return true;
    })
    .filter(posFilter)
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = orderBy === 'rk' ? (consensusRankMap[a.name] ?? Infinity) : (a[orderBy] ?? (order==='asc' ? Infinity : -Infinity));
      const bv = orderBy === 'rk' ? (consensusRankMap[b.name] ?? Infinity) : (b[orderBy] ?? (order==='asc' ? Infinity : -Infinity));
      return order === 'asc' ? av - bv : bv - av;
    });

  const handleSort = key => {
    if (orderBy === key) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(key); setOrder(key === 'nr' ? 'desc' : 'asc'); }
  };

  const SortCell = ({ colKey, label, tip, defaultOrder = 'asc' }) => (
    <TableCell align="center" sx={{ ...TH, textAlign: 'center' }}>
      <Tooltip title={tip} placement="top" arrow>
        <TableSortLabel active={orderBy===colKey} direction={orderBy===colKey?order:defaultOrder} onClick={()=>handleSort(colKey)}
          sx={{ '& .MuiTableSortLabel-icon': { color: '#f5a623 !important' }, color: 'inherit' }}>
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  const visibleRows = (isPremium) ? sorted : sorted.slice(0, FREE_PREVIEW_ROWS);
  const lockedCount = sorted.length - visibleRows.length;

  return (
    <Box sx={CARD}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(245,166,35,0.15)', backgroundColor: '#0e0e12', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', color: '#f5a623', textTransform: 'uppercase' }}>
            🎯 Consensus FA Board 2026
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', color: '#64748b', mt: 0.25 }}>
            Avg of: Prisco (CBS) · Rosenthal (NFL.com) · vs our Nerd Rating
          </Typography>
        </Box>
        <ToggleButtonGroup value={pos} exclusive onChange={(_,v)=>v&&setPos(v)} size="small">
          {POS_FILTERS.map(g => (
            <ToggleButton key={g} value={g} sx={{
              color: pos===g ? '#f5a623' : '#64748b', borderColor: 'rgba(245,166,35,0.2)',
              backgroundColor: pos===g ? 'rgba(245,166,35,0.08)' : 'transparent',
              fontWeight: 800, fontSize: '0.62rem', px: 1.1, py: 0.35,
              '&:hover': { backgroundColor: 'rgba(245,166,35,0.05)' },
            }}>{g}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField size="small" placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><span style={{color:'#475569',fontSize:'0.8rem'}}>🔍</span></InputAdornment> }}
          sx={{ width: 150, ml: 'auto', '& .MuiOutlinedInput-root': { backgroundColor: '#0a0a0a', fontSize: '0.8rem' },
            '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(245,166,35,0.2)' },
            '& input': { color: '#ffffff', py: 0.65 } }} />
      </Box>

      <Box sx={{ overflowX: 'auto', ...SCROLLBAR }}>
        <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow>
              <SortCell colKey="rk" label="Rk" tip="Consensus rank — average of all available analyst rankings (lower = better)" />
              <TableCell sx={{ ...TH, width: 40 }}>Pos</TableCell>
              <TableCell sx={TH}>Player</TableCell>
              <SortCell colKey="prisco"     label="CBS"        tip="Pete Prisco — CBS Sports" />
              <SortCell colKey="rosenthal"  label="NFL.com"    tip="Gregg Rosenthal — NFL.com" />
              <SortCell colKey="bowen"      label="ESPN"       tip="Matt Bowen — ESPN" />
              <SortCell colKey="pff"        label="PFF"         tip="Pro Football Focus rank" />
              <SortCell colKey="nr"         label="NR"         tip="Our Nerd Rating rank within position group (lower = better)" />
              <SortCell colKey="projectedAPY" label="Est. APY" tip="Projected new contract APY — community estimates, not official. Updated as deals are signed." defaultOrder="desc" />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((p) => {
              const nrDiff = p.nrRank != null && p.consensus != null
                ? p.nrRank - p.consensus  // negative = we like them more than analysts (lower rank = better)
                : null;
              // eslint-disable-next-line no-unused-vars
              const weAgree = nrDiff != null && Math.abs(nrDiff) < 10;
              const weLikeMore = nrDiff != null && nrDiff < -15;
              const theyLikeMore = nrDiff != null && nrDiff > 15;
              return (
                <TableRow key={p.name} sx={{ '& td': { borderBottom: '1px solid rgba(245,166,35,0.06)' }, '&:hover td': { backgroundColor: 'rgba(245,166,35,0.04)' } }}>
                  <TableCell sx={{ py: 0.5, color: '#f5a623', fontWeight: 800, fontSize: '0.7rem', textAlign: 'center', backgroundColor: 'transparent' }}>{consensusRankMap[p.name]}</TableCell>
                  <TableCell sx={{ py: 0.5, backgroundColor: 'transparent' }}>
                    <Chip label={p.pos} size="small" sx={{ height: 17, fontSize: '0.6rem', fontWeight: 800, bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid rgba(245,166,35,0.25)' }} />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, backgroundColor: 'transparent' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap' }}>{p.name}</Typography>
                      {weLikeMore && (
                        <Tooltip title="Our NR rates this player higher than analyst consensus" arrow>
                          <Chip label="📈 NR+" size="small" sx={{ height: 15, fontSize: '0.52rem', fontWeight: 900, bgcolor: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid #10b98130' }} />
                        </Tooltip>
                      )}
                      {theyLikeMore && (
                        <Tooltip title="Analysts rank this player higher than our NR suggests" arrow>
                          <Chip label="📣 Hype?" size="small" sx={{ height: 15, fontSize: '0.52rem', fontWeight: 900, bgcolor: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid #ef444430' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  {['prisco','rosenthal','bowen','pff'].map(src => (
                    <TableCell key={src} align="center" sx={{ py: 0.5, px: 0.75, backgroundColor: 'transparent', color: '#ffffff', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', width: 44, minWidth: 44 }}>
                      {p[src] != null ? `#${p[src]}` : <span style={{color:'#334155'}}>—</span>}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ py: 0.5, px: 0.75, backgroundColor: 'transparent', color: p.nrRank ? '#10b981' : '#334155', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem', width: 44, minWidth: 44 }}>
                    {p.nrRank != null ? `#${p.nrRank}` : '—'}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5, backgroundColor: 'transparent', color: p.projectedAPY ? '#f5a623' : '#334155', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem' }}>
                    {p.projectedAPY ? `$${p.projectedAPY}M` : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {/* Free teaser — upgrade prompt below top-10 rows */}
        {lockedCount > 0 && (
          <Box sx={{ position: 'relative', mt: 0 }}>
            {/* Ghost rows */}
            {Array.from({ length: Math.min(lockedCount, 5) }).map((_, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.8, opacity: 0.13 - i * 0.02, filter: 'blur(3px)', borderBottom: '1px solid rgba(245,166,35,0.04)' }}>
                <Box sx={{ width: 32, height: 10, borderRadius: 1, bgcolor: '#f5a623' }} />
                <Box sx={{ width: 40, height: 10, borderRadius: 1, bgcolor: '#64748b' }} />
                <Box sx={{ flex: 1, height: 10, borderRadius: 1, bgcolor: '#334155' }} />
                <Box sx={{ width: 30, height: 10, borderRadius: 1, bgcolor: '#1e293b' }} />
                <Box sx={{ width: 30, height: 10, borderRadius: 1, bgcolor: '#1e293b' }} />
                <Box sx={{ width: 30, height: 10, borderRadius: 1, bgcolor: '#1e293b' }} />
              </Box>
            ))}
            {/* Upgrade CTA */}
            <Box sx={{
              textAlign: 'center', py: 2, px: 2,
              borderTop: '1px solid rgba(245,166,35,0.15)',
              background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
            }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#9eb8d0', mb: 1 }}>
                <strong style={{ color: '#f5a623' }}>{lockedCount} more players</strong> ranked — see the full board
              </Typography>
              {isBasic ? (
                <Button size="small" variant="contained" color="primary" onClick={() => setAuthModalOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', mr: 1 }}>
                  Sign Up Free →
                </Button>
              ) : (
                <Button size="small" variant="contained" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', bgcolor: '#f5a623', color: '#000', '&:hover': { bgcolor: '#d4890f' } }}
                  onClick={() => setUpgradeModalOpen(true)}>
                  Unlock Nerds+ →
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid rgba(245,166,35,0.1)', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}><Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor:'#10b981' }}/><Typography sx={{fontSize:'0.62rem',color:'#64748b'}}>📈 NR+ = we rate higher than analysts</Typography></Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}><Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor:'#ef4444' }}/><Typography sx={{fontSize:'0.62rem',color:'#64748b'}}>📣 Hype? = analysts rate higher than NR</Typography></Box>
        </Box>
        <Typography sx={{ fontSize: '0.62rem', color: '#334155', ml: 'auto' }}>
          CBS (Prisco) · NFL.com (Rosenthal) · ESPN (Bowen) · PFF · Est. updated Mar 6
        </Typography>
      </Box>
    </Box>
  );
}
